import { useEffect, useState } from "react";
import {
  buildOfferFormData,
  getofferbyid,
  saveBrandOffer,
  updateOfferByBrand,
} from "../../../apiService/loyalty.service";

const formatDateInputValue = (value) => {
  if (!value) return "";

  const trimmedValue = String(value).trim();
  if (!trimmedValue) return "";

  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmedValue)) {
    return trimmedValue;
  }

  const parsedDate = new Date(trimmedValue);
  if (Number.isNaN(parsedDate.getTime())) {
    return trimmedValue;
  }

  return new Date(parsedDate.getTime() + parsedDate.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 10);
};

const initialOfferForm = {
  title_en: "",
  title_hi: "",
  tagline_en: "",
  tagline_hi: "",
  description_en: "",
  description_hi: "",
  offer_banner: "",
  offer_banner_file: null,
  redirection_url: "",
  save_amount: "",
  start_date: "",
  end_date: "",
  redemption_price: "",
  redemption_type: "",
  status: "active",
  featured: false,
};

const normalizeOfferForm = (initialValues = {}) => ({
  title_en: initialValues.offer_title_en || initialValues.title_en || "",
  title_hi: initialValues.offer_title_hi || initialValues.title_hi || "",
  tagline_en: initialValues.offer_tagline_en || initialValues.tagline_en || "",
  tagline_hi: initialValues.offer_tagline_hi || initialValues.tagline_hi || "",
  description_en: initialValues.offer_description_en || initialValues.description_en || "",
  description_hi: initialValues.offer_description_hi || initialValues.description_hi || "",
  offer_banner: initialValues.offer_banner || "",
  offer_banner_file: null,
  redirection_url: initialValues.redirection_url || "",
  save_amount: initialValues.save_amount ?? "",
  start_date: formatDateInputValue(initialValues.start_date),
  end_date: formatDateInputValue(initialValues.end_date),
  redemption_price: initialValues.redemption_price ?? "",
  redemption_type: initialValues.redemption_type || "",
  status: initialValues.status || "active",
  featured: Boolean(initialValues.is_featured ?? initialValues.featured),
});

export const useOfferEntryForm = ({ brandId, offerId, initialValues, onSaved }) => {
  const isEdit = Boolean(offerId);
  const [form, setForm] = useState(initialOfferForm);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isEdit || !offerId) {
      if (!initialValues) {
        setForm(initialOfferForm);
      } else {
        setForm(normalizeOfferForm(initialValues));
      }
      return;
    }

    let cancelled = false;

    const loadOffer = async () => {
      try {
        const response = await getofferbyid(offerId);
        const fetchedOffer = response && typeof response === "object" ? response : null;

        if (!cancelled) {
          setForm(fetchedOffer ? normalizeOfferForm(fetchedOffer) : normalizeOfferForm(initialValues || {}));
        }
      } catch (fetchError) {
        if (!cancelled) {
          setForm(normalizeOfferForm(initialValues || {}));
        }
      }
    };

    loadOffer();

    return () => {
      cancelled = true;
    };
  }, [initialValues, brandId, isEdit, offerId]);

  const handleFieldChange = (event) => {
    const { name, value } = event.target;
    setFieldErrors((prev) => ({ ...prev, [name]: "" }));
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (event) => {
    const file = event.target.files?.[0] || null;
    setFieldErrors((prev) => ({ ...prev, offer_banner_file: "" }));
    setForm((prev) => ({
      ...prev,
      offer_banner_file: file,
      offer_banner: file ? URL.createObjectURL(file) : prev.offer_banner,
    }));
  };

  const handleToggleBoolean = (field) => {
    setForm((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const plainDescriptionEn = (form.description_en || "").replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim();
    const plainDescriptionHi = (form.description_hi || "").replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim();
    const nextFieldErrors = {};

    if (!form.title_en.trim()) {
      nextFieldErrors.title_en = "Offer title in English is required.";
    }
    if (!form.redemption_price) {
      nextFieldErrors.redemption_price = "Redemption price is required.";
    }
    if (!form.redemption_type) {
      nextFieldErrors.redemption_type = "Redemption type is required.";
    }
    if (!form.redirection_url.trim()) {
      nextFieldErrors.redirection_url = "Redirection URL is required.";
    }
    if (!form.start_date.trim()) {
      nextFieldErrors.start_date = "Start date is required.";
    }
    if (!form.end_date.trim()) {
      nextFieldErrors.end_date = "End date is required.";
    }
    if (!plainDescriptionEn) {
      nextFieldErrors.description_en = "Description in English is required.";
    }
    if (!plainDescriptionHi && form.description_hi) {
      nextFieldErrors.description_hi = "Description in Hindi is not valid.";
    }

    const hasOfferImage =
      Boolean(form.offer_banner_file) ||
      (typeof form.offer_banner === "string" && form.offer_banner.trim().length > 0);

    if (!isEdit && !hasOfferImage) {
      nextFieldErrors.offer_banner_file = "Offer banner image is required for a new offer.";
    }

    setFieldErrors(nextFieldErrors);

    if (Object.keys(nextFieldErrors).length > 0) {
      setError(Object.values(nextFieldErrors)[0]);
      return;
    }

    setError("");
    setIsSubmitting(true);

    try {
      const payload = buildOfferFormData({
        id: offerId,
        brandId,
        offer_title_en: form.title_en,
        offer_title_hi: form.title_hi,
        offer_tagline_en: form.tagline_en,
        offer_tagline_hi: form.tagline_hi,
        offer_description_en: form.description_en,
        offer_description_hi: form.description_hi,
        offer_banner: form.offer_banner_file || form.offer_banner,
        redirection_url: form.redirection_url,
        save_amount: form.save_amount,
        start_date: form.start_date,
        end_date: form.end_date,
        redemption_price: form.redemption_price,
        redemption_type: form.redemption_type,
      });

      if (isEdit) {
        await updateOfferByBrand(brandId, payload);
      } else {
        await saveBrandOffer(brandId, payload);
      }

      setFieldErrors({});
      onSaved?.();
    } catch (submitError) {
      const message =
        submitError?.response?.data?.message ||
        submitError?.message ||
        "Failed to save offer. Please try again.";
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    isEdit,
    form,
    error,
    fieldErrors,
    isSubmitting,
    handleFieldChange,
    handleImageChange,
    handleToggleBoolean,
    handleSubmit,
  };
};

export default useOfferEntryForm;
